import {
  PRODUCT_REVIEW_DATA_LOADED,
  PRODUCT_REVIEW_DATA_CLEAR,
  PRODUCT_REVIEW_STATS_LOADED,
  PRODUCT_REVIEW_STATS_CLEAR,
  PRODUCT_REVIEW_META_LOADED,
  PRODUCT_REVIEW_META_CLEAR,
} from '../actionTypes';

import * as api from '../apiHelper';
import {
  setFetchLoaderLoading,
  setFetchLoaderReset,
  setSubmissionLoaderLoading,
  setSubmissionLoaderReset,
} from '../fetchLoaderActions';
import { arriveNotice } from '../noticeActions';
import { setFaviconLoading, setFaviconDefault } from '../faviconActions';

// Reducer corresponding actions
function productReviewClear() {
  return { type: PRODUCT_REVIEW_DATA_CLEAR };
}

function productReviewLoaded(result) {
  return {
    type: PRODUCT_REVIEW_DATA_LOADED,
    payload: { result },
  };
}

function productReviewStatsClear() {
  return { type: PRODUCT_REVIEW_STATS_CLEAR };
}

function productReviewStatsLoaded(result) {
  return {
    type: PRODUCT_REVIEW_STATS_LOADED,
    payload: { result },
  };
}

function productReviewMetaClear() {
  return { type: PRODUCT_REVIEW_META_CLEAR };
}

function productReviewMetaLoaded(result) {
  return {
    type: PRODUCT_REVIEW_META_LOADED,
    payload: { result },
  };
}

// API
async function getProductReviewApi(args, token, isCSV) {
  if (isCSV) {
    await api.getCSV({
      path: 'results/review',
      params: args,
      token,
      version: 'v3',
      filename: 'redflag-review-export',
    });
    return null;
  }

  const response = await api.nestedParamsGet({
    path: 'results/review',
    params: args,
    token,
    version: 'v3',
  });
  return response.json();
}

async function getProductReviewStatsApi(args, token) {
  const response = await api.get({
    path: 'results/review/stats',
    params: args,
    token,
    version: 'v3',
  });
  return response.json();
}

async function getProductReviewMetaApi(args, token) {
  const response = await api.get({
    path: 'results/review/meta',
    params: args,
    token,
    version: 'v3',
  });
  return response.json();
}

async function updateProductReviewSelectionsApi(body, token) {
  const response = await api.put({
    path: 'results/review',
    body,
    token,
    version: 'v3',
  });
  return response;
}

async function bulkUpdateReviewStatusByDomainApi(body, token) {
  const response = await api.post({
    path: 'results/review/bulk_update_by_domain',
    body,
    token,
    version: 'v3',
  });
  return response.json();
}

// Helper
async function fetchProductReview(dispatch, args, token, isCSV) {
  try {
    const response = await getProductReviewApi(args, token, isCSV);

    if (!isCSV) {
      dispatch(setFaviconDefault());
      dispatch(setFetchLoaderReset());
      dispatch(productReviewLoaded(response));
    }

    if (isCSV) dispatch(setSubmissionLoaderReset());
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setFaviconDefault());
      dispatch(arriveNotice(e.message));
      if (isCSV) dispatch(setSubmissionLoaderReset());
    }
  }
}

async function fetchProductReviewStats(dispatch, args, token) {
  try {
    const response = await getProductReviewStatsApi(args, token);

    dispatch(setFaviconDefault());
    dispatch(productReviewStatsLoaded(response));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setFaviconDefault());
      dispatch(arriveNotice(e.message));
    }
  }
}

async function fetchProductReviewMeta(dispatch, args, token) {
  try {
    const response = await getProductReviewMetaApi(args, token);

    dispatch(setFaviconDefault());
    dispatch(productReviewMetaLoaded(response));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setFaviconDefault());
      dispatch(arriveNotice(e.message));
    }
  }
}

// Public components actions
export function clearProductReviewRequest() {
  return (dispatch) => {
    dispatch(productReviewClear());
  };
}

export function initiateProductReviewRequest(args, isCSV = false) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      favicon: { faviconStatus },
      fetchLoader: { dataLoading, submissionLoading },
    } = getState();

    if (!dataLoading && !isCSV) {
      dispatch(setFetchLoaderLoading());
    } else if (!submissionLoading && isCSV) {
      dispatch(setSubmissionLoaderLoading());
    }

    if (faviconStatus !== 'loading' && !isCSV) {
      dispatch(setFaviconLoading());
    }

    await fetchProductReview(dispatch, args, token, isCSV);
  };
}

export function clearProductReviewStatsRequest() {
  return (dispatch) => {
    dispatch(productReviewStatsClear());
  };
}

export function initiateProductReviewStatsRequest(args) {
  return async (dispatch, getState) => {
    const { auth: { token }, favicon: { faviconStatus } } = getState();

    if (faviconStatus !== 'loading') {
      dispatch(setFaviconLoading());
    }

    await fetchProductReviewStats(dispatch, args, token);
  };
}

export function clearProductReviewMetaRequest() {
  return (dispatch) => {
    dispatch(productReviewMetaClear());
  };
}

export function initiateProductReviewMetaRequest(args) {
  return async (dispatch, getState) => {
    const { auth: { token }, favicon: { faviconStatus } } = getState();

    if (faviconStatus !== 'loading') {
      dispatch(setFaviconLoading());
    }

    await fetchProductReviewMeta(dispatch, args, token);
  };
}

export function updateProductReviewSelectionsRequest(body, afterAction) {
  return async (dispatch, getState) => {
    const { auth: { token } } = getState();

    try {
      const { notice } = await updateProductReviewSelectionsApi(body, token);

      dispatch(arriveNotice(notice, 'ok'));
      afterAction();
    } catch (e) {
      dispatch(arriveNotice(e.message));
    }
  };
}

export function bulkUpdateReviewStatusByDomainRequest(body, afterAction) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      fetchLoader: { submissionLoading },
    } = getState();

    if (!submissionLoading) {
      dispatch(setSubmissionLoaderLoading());
    }

    try {
      const response = await bulkUpdateReviewStatusByDomainApi(body, token);

      dispatch(setSubmissionLoaderReset());

      dispatch(arriveNotice(response.message, 'ok'));
      afterAction();
    } catch (e) {
      dispatch(setSubmissionLoaderReset());
      dispatch(arriveNotice(e.message));
    }
  };
}
